/**
 * 기준정보 화면 공용 조각.
 *
 * 디자인 원칙(CLAUDE.md §UI 1) — Apple 스타일 미니멀 (주색은 주 동작·선택 표시에만).
 * 아이콘·이모지·KPI 요약카드·박스 장식을 쓰지 않고, 위계는 타이포와 여백·헤어라인으로만 만든다.
 * 로딩·빈 상태·에러도 스피너 없이 텍스트로 처리한다.
 */
import {
  Children,
  Fragment,
  createContext,
  isValidElement,
  useContext,
  useEffect,
  useState,
} from "react";
import type { ReactElement, ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import SearchableDropdown from "@/components/base/SearchableDropdown";
import StatusMessage from "@/components/base/StatusMessage";
import Working from "@/components/base/Working";
import type { StatusMessageValue } from "@/components/base/StatusMessage";
import { buttonClass } from "@/components/base/buttonStyles";
import type { DeletePlan } from "@/api/masterBase";

// ---------------------------------------------------------------------------
// 머리말·탭
// ---------------------------------------------------------------------------

/**
 * 상위 목록으로 돌아가는 링크. 등록·수정처럼 목록에서 파고든 화면의 머리말 위에 둔다.
 * 브라우저 뒤로가기만으로는 어디로 돌아가는지 알 수 없어 경로를 글자로 밝힌다.
 */
export function BackLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link
      to={to}
      className="mb-2 inline-block text-xs text-gray-400 transition-colors hover:text-gray-900"
    >
      ← {children}
    </Link>
  );
}

export function PageHeading({
  title,
  description,
  notice,
  actions,
  backTo,
  backLabel,
}: {
  title: string;
  description?: ReactNode;
  /**
   * 머리말 아래 한 줄 — **그 건의 지금 상태**를 적는다.
   *
   * 2026-10-01 에 고정 설명문은 전부 도움말로 갔고, 여기 남은 것은 담당자가 **손대야
   * 하는 것**뿐이다(송장 검수의 미분류·재제출·중복·반려, 자사정보의 기본값 안내).
   * 그래서 실제 호출부는 전부 `warnNotice()`·`statusNotice()` 또는
   * `useStatusMessage().value` 다 — **평문 `info` 로 넘기는 곳은 없다.**
   *
   * 팩토리가 만든 것은 `inline: true` 를 지녀 흐름 안에 남고, 훅이 만든 것은 떠 있는
   * 알림으로 간다(2026-10-02). 그 가름의 근거는 `StatusMessage` 주석에 있다.
   */
  notice?: ReactNode | StatusMessageValue;
  actions?: ReactNode;
  /** 돌아갈 상위 목록 경로 */
  backTo?: string;
  backLabel?: string;
}) {
  return (
    <div className="mb-6">
      {backTo && <BackLink to={backTo}>{backLabel ?? "목록으로"}</BackLink>}
      <div className="flex items-baseline justify-between gap-6">
        <h2 className={TITLE_CLASS}>{title}</h2>
        {actions && <div className="flex items-center gap-2 text-sm">{actions}</div>}
      </div>
      {description && (
        <p className="mt-1.5 max-w-3xl text-xs leading-relaxed text-gray-500">{description}</p>
      )}
      {isStatusValue(notice) ? (
        <StatusMessage value={notice} className="mt-2 mb-0 max-w-3xl" />
      ) : (
        notice && (
          <StatusMessage tone="info" className="mt-2 mb-0 max-w-3xl">
            {notice}
          </StatusMessage>
        )
      )}
    </div>
  );
}

/** 화면 제목의 생김새. `PageHeading` 과 `Tabs title` 이 갈리지 않게 한 곳에 둔다 */
const TITLE_CLASS = "text-xl font-semibold tracking-tight text-gray-900";

export interface TabItem {
  key: string;
  label: string;
  /** 라벨 뒤에 붙는 보조 숫자·설명 */
  hint?: ReactNode;
}

export function Tabs({
  tabs,
  value,
  onChange,
  title,
  actions,
}: {
  tabs: TabItem[];
  value: string;
  onChange: (key: string) => void;
  /**
   * 화면 제목을 **탭과 같은 줄에** 그린다 (2026-09-28).
   *
   * 탭 화면은 머리말 블록(제목 + 설명 + 마진)과 탭 줄이 따로 서서 147px 을 먹었다.
   * 표가 화면을 채우는 화면에서는 그 절반이 표로 가는 편이 낫다 — 차장님이
   * *"스크롤을 안내리고 11대를 한 화면에 보고싶다"* 고 한 배차 등록이 그 경우다.
   * 제목이 있으면 그 줄이 머리말 블록 전체이므로 **스스로 간격을 갖는다**(`mb-4`).
   *
   * 넘기지 않으면 지금까지와 똑같다 — 기존 호출부는 손대지 않았다.
   */
  title?: string;
  /**
   * 머리말 버튼을 **탭과 같은 줄 오른쪽에** 둔다 (2026-10-01).
   *
   * `title` 로 제목을 탭 줄에 올리면 `PageHeading` 이 사라지는데, 그 화면들이 넘기던
   * `actions` 가 갈 곳이 없었다. 제목·탭·버튼 세 덩이가 한 줄에 서지만 탭은 가운데를
   * 쓰지 않고 왼쪽에 모이므로 오른쪽이 비어 있다 — 버튼이 넷을 넘어 좁아지면 그
   * 화면만 `Screen toolbar` 로 내린다.
   */
  actions?: ReactNode;
}) {
  return (
    <div
      className={`flex items-center gap-6 border-b border-gray-200 ${
        title ? "mb-4" : "mb-5"
      }`}
    >
      {title && <h2 className={`${TITLE_CLASS} mr-2 pb-2`}>{title}</h2>}
      {tabs.map((tab) => {
        const active = tab.key === value;
        return (
          <button
            key={tab.key}
            type="button"
            onClick={() => onChange(tab.key)}
            className={`-mb-px border-b-2 pb-2 text-sm transition-colors ${
              active
                ? "border-primary-600 font-medium text-primary-700"
                : "border-transparent text-gray-400 hover:text-gray-700"
            }`}
          >
            {tab.label}
            {tab.hint !== undefined && tab.hint !== null && (
              <span className="ml-1.5 text-xs text-gray-400">{tab.hint}</span>
            )}
          </button>
        );
      })}
      {actions && (
        // `pb-2` 로 탭 버튼과 밑선을 맞춘다 — 헤어라인 위에 나란히 앉는다.
        <div className="ml-auto flex items-center gap-2 pb-2 text-sm">{actions}</div>
      )}
    </div>
  );
}

/**
 * 장소 ↔ 구간 전환 탭.
 *
 * 두 화면은 사이드바 메뉴 하나(`구간·장소`)에 묶여 있는데, 예전에는 오른쪽 위 보조 버튼
 * (`구간 관리`·`장소 관리`)으로만 오갈 수 있었다. 보조 버튼 자리는 `신규 등록` 같은
 * "이 화면에서 할 일"이 놓이는 곳이라, 화면이 두 개고 서로 오갈 수 있다는 것이 보이지 않았다.
 * 머리말 바로 아래 왼쪽에 탭으로 두어 지금 어디에 있고 어디로 갈 수 있는지 드러낸다
 * (단가 화면의 매출/매입 탭과 같은 자리·같은 모양이다).
 */
export function LocationRouteTabs({ value }: { value: "location" | "route" }) {
  const navigate = useNavigate();
  return (
    <Tabs
      tabs={[
        { key: "route", label: "구간" },
        { key: "location", label: "장소" },
      ]}
      value={value}
      onChange={(key) => navigate(`/admin/master/${key}`)}
    />
  );
}

// ---------------------------------------------------------------------------
// 필터
// ---------------------------------------------------------------------------

/**
 * 지금 걸려 있는 필터 하나 (설계원칙 P14·P15).
 * 대개 `FilterBar` 가 자기 자식(`SelectField`·`TextField`)에서 직접 만들어 낸다.
 */
export interface ActiveFilter {
  /** 어느 칸의 조건인지 — `차량`, `상태` */
  label: string;
  /** 사람이 읽는 값 — 코드가 아니라 드롭다운에 보이던 그 글자 */
  value: string;
  onClear: () => void;
}

/**
 * 드롭다운 필터를 칩으로 바꾼다. 화면에 보이던 라벨을 옵션 목록에서 되찾아 쓴다 —
 * 칩에 `12` 같은 id 가 찍히면 무엇이 걸렸는지 여전히 알 수 없다.
 */
export function selectFilter(
  label: string,
  value: string,
  options: readonly SelectOption[],
  onClear: () => void
): ActiveFilter | null {
  if (!value) return null;
  const found = options.find((option) => option.value === value);
  return { label, value: found?.label ?? value, onClear };
}

/** 검색어처럼 값이 곧 표시인 필터 */
export function textFilter(
  label: string,
  value: string,
  onClear: () => void
): ActiveFilter | null {
  const trimmed = value.trim();
  return trimmed === "" ? null : { label, value: trimmed, onClear };
}

/**
 * 필터바의 자식에서 걸린 조건을 직접 읽어 낸다.
 *
 * 화면마다 손으로 목록을 적게 하면 **필터를 하나 추가할 때 칩을 빠뜨린다.** 빠뜨린 칩은
 * 없는 것보다 나쁘다 — 목록에 안 보이니 안 걸린 줄 알게 된다. 그래서 필터바가 자기
 * 자식(`SelectField`·`TextField`)을 그대로 읽는다.
 *
 * 지울 수 없는 칸(배차등록의 배차일자처럼 조회 조건이 아니라 입력값인 것)은
 * `clearable={false}` 로 뺀다.
 */
function derivedFilters(children: ReactNode): ActiveFilter[] {
  const found: ActiveFilter[] = [];
  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;
    const element = child as ReactElement<Record<string, unknown>>;
    if (element.type === Fragment) {
      found.push(...derivedFilters(element.props.children as ReactNode));
      return;
    }
    const isSelect = element.type === SelectField;
    if (!isSelect && element.type !== TextField) return;

    const props = element.props as {
      label?: string;
      value?: string;
      options?: readonly SelectOption[];
      onChange?: (value: string) => void;
      clearable?: boolean;
    };
    if (props.clearable === false || !props.label || !props.onChange) return;

    const clear = () => props.onChange?.("");
    const filter = isSelect
      ? selectFilter(props.label, props.value ?? "", props.options ?? [], clear)
      : textFilter(props.label, props.value ?? "", clear);
    if (filter) found.push(filter);
  });
  return found;
}

/**
 * 필터바 (설계원칙 P14·P15).
 *
 * 필터 문법은 사용자에게 투명해야 한다. 예전에는 무엇이 걸렸는지 알려면 드롭다운을 하나씩
 * 열어봐야 했고, 다섯 개를 되돌리려면 다섯 번 열어야 했다. **표시되지 않은 필터는 사용자가
 * 데이터를 오독하게 만든다** — 필터가 걸린 줄 모르고 톤수 합계를 읽는 것이 이 시스템에서
 * 가장 비싼 실패다.
 *
 * 그래서 걸린 조건을 칩으로 늘어놓고 하나씩·한꺼번에 풀 수 있게 한다. 칩은 누를 수 있으니
 * 테두리와 여백을 준다(UI 규칙 3) — 장식이 아니라 컨트롤이라 `buttonClass` 를 그대로 쓴다.
 * 아이콘을 쓰지 않으므로 `×` 대신 `해제` 라고 적는다.
 */
/**
 * 지금 그리는 `Field` 가 **조회 조건 줄 안에 있는지** 알려 준다 (2026-10-01).
 *
 * 조회 조건은 라벨을 입력칸 **왼쪽**에 두어 줄 높이를 53px → 33px 로 줄인다. `Screen` 이
 * 머리말·조회조건을 `shrink-0` 으로 두므로 그 20px 이 그대로 표로 간다.
 *
 * 모달·입력 폼은 상하 배치를 유지한다 — 폭이 좁아 라벨을 왼쪽에 두면 입력칸이 눌리고,
 * 여러 칸을 위에서 아래로 훑어 읽는 폼에서는 라벨이 위에 있는 편이 낫다. 기본값이
 * `false` 이므로 **`FilterBar` 밖은 아무것도 안 해도 지금 그대로다.**
 */
const FieldInline = createContext(false);

export function FilterBar({
  children,
  active,
  onClearAll,
  chips = true,
  inline = true,
}: {
  children: ReactNode;
  /** 자식에서 못 읽는 필터(직접 만든 조각·기간 선택 등)를 더할 때 */
  active?: (ActiveFilter | null)[];
  /** 전부 푸는 동작. 주지 않으면 읽어 낸 칩들을 차례로 푼다 */
  onClearAll?: () => void;
  /** 조회 조건이 아니라 입력 폼으로 쓰는 필터바는 끈다 (배차등록 등) */
  chips?: boolean;
  /**
   * 라벨을 입력칸 왼쪽에 둔다. 기본값이 `true` 다.
   *
   * **`chips` 로 가르지 않는다** — `월간 계획` 은 목록을 거르는 조회 폼인데 계획 월이 늘
   * 걸려 있어 칩이 소음이라 `chips={false}` 다. 그것으로 배치를 가르면 그 조회 폼만
   * 상하로 남는다. 끄는 곳은 `배차 등록`(`CreateForm`) 하나뿐이고, 거기는 차량 칸
   * 라벨 오른쪽에 `Field action`(배정/전체 토글)이 앉아 있어 자리를 비켜 줄 수 없다.
   */
  inline?: boolean;
}) {
  const derived = chips ? derivedFilters(children) : [];
  const extra = (active ?? []).filter((item): item is ActiveFilter => item !== null);
  const applied = [...derived, ...extra];
  const clearAll = onClearAll ?? (() => applied.forEach((filter) => filter.onClear()));

  return (
    <div className="mb-5">
      {/*
        `items-end` 를 유지한다 — 필터바 안 버튼들이 `pb-1.5` 로 이 밑선에 맞춰 서 있고
        (기사 계정·집계표·단가 5곳), 칸이 53→33px 로 낮아져도 **칸 밑선 = 입력칸 하단선**은
        그대로다. `items-center` 로 바꾸면 그 버튼들이 전부 어긋난다.
      */}
      <div className="flex flex-wrap items-end gap-x-6 gap-y-3">
        <FieldInline.Provider value={inline}>{children}</FieldInline.Provider>
      </div>

      {applied.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="text-xs text-gray-400">필터 적용중</span>
          {applied.map((filter) => (
            <button
              key={`${filter.label}:${filter.value}`}
              type="button"
              onClick={filter.onClear}
              className={buttonClass("secondary", "small")}
            >
              {filter.label}:{" "}
              <span className="font-medium text-gray-900">{filter.value}</span>
              <span className="ml-1.5 text-gray-400">해제</span>
            </button>
          ))}
          {applied.length > 1 && (
            <button
              type="button"
              onClick={clearAll}
              className={`${buttonClass("secondary", "small")} ml-1`}
            >
              필터 초기화
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * 라벨 + 입력칸 한 칸. **앱에서 라벨을 그리는 단일 지점이다** — `TextField`·`SelectField`
 * 가 모두 이것으로 감싸므로 여기 한 곳이 조회조건 25곳과 모달·폼 전부에 퍼진다.
 *
 * 배치는 `FilterBar` 가 context 로 정한다 (`FieldInline` 주석 참고).
 *
 * **좌우 모드에서 `width` 는 입력칸 폭이 아니라 칸 전체(라벨+입력) 폭이다.** 입력칸은
 * `flex-1` 로 남은 자리를 먹는다 — 그래서 **조회 조건 줄의 가로 폭이 상하 배치와 똑같고
 * 줄바꿈이 새로 생기지 않는다.** 라벨을 입력칸 왼쪽에 "덧붙이는" 방식으로 하면 6~7칸
 * 화면(단가·구간)이 한 줄을 넘겨 20px 을 벌려다 25px 을 잃는다(`gap-y-3` 12px + 33px).
 * 대가는 입력칸이 라벨만큼 좁아지는 것이고, 그래서 좁은 칸은 호출부에서 폭을 올리거나
 * 라벨을 줄인다.
 */
export function Field({
  label,
  width = "w-40",
  action,
  children,
}: {
  label: string;
  width?: string;
  /** 라벨 오른쪽에 붙는 작은 컨트롤. 그 칸에만 걸리는 토글을 여기에 둔다 */
  action?: ReactNode;
  children: ReactNode;
}) {
  const inline = useContext(FieldInline);

  if (inline) {
    return (
      <div className={`flex items-baseline gap-2 ${width}`}>
        <span className="shrink-0 text-xs text-gray-500">{label}</span>
        {/* `min-w-0` 가 없으면 긴 값이 칸을 밀어내 줄이 찢어진다 */}
        <div className="min-w-0 flex-1">{children}</div>
        {action}
      </div>
    );
  }

  return (
    <div className={width}>
      <div className="mb-1 flex items-baseline justify-between gap-2 text-xs text-gray-500">
        <span>{label}</span>
        {action}
      </div>
      {children}
    </div>
  );
}

export function TextField({
  label,
  value,
  onChange,
  onEnter,
  placeholder = "",
  width = "w-56",
  type = "text",
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  /**
   * Enter 로 제출한다. 모달 안 입력칸에 쓴다 — `Modal` 은 바닥 버튼이 `footer` 로
   * 빠져 있어 `<form>` 으로 묶을 수 없고, 그대로 두면 값을 치고 나서 마우스로만 누를 수 있다.
   */
  onEnter?: () => void;
  placeholder?: string;
  width?: string;
  /** `password` 는 화면에 값을 가린다 — 남이 보는 앞에서 비밀번호를 발급하는 자리용 */
  type?: "text" | "date" | "month" | "number" | "password";
  /** 조회 조건이 아니라 입력값이면 `false` — 활성 필터 칩에서 뺀다 (`FilterBar` 참고) */
  clearable?: boolean;
}) {
  return (
    <Field label={label} width={width}>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={
          onEnter
            ? (e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  onEnter();
                }
              }
            : undefined
        }
        className="w-full border-b border-gray-200 bg-transparent py-1.5 text-sm text-gray-900 placeholder:text-gray-300 focus:border-primary-600 focus:outline-none"
      />
    </Field>
  );
}

/**
 * 여러 줄 입력 — 이의신청 회신·공지 본문·반려 사유용.
 *
 * 표 인라인 편집은 한 줄에 들어가는 값을 전제한 규칙이다(CLAUDE.md §UI 2). 여러 줄 텍스트를
 * 셀에 넣으면 행 높이가 들쭉날쭉해져 인라인 편집이 지키려던 스캔성이 무너지므로, 긴 본문은
 * 모달 안에서 이 조각으로 받는다.
 *
 * 테두리는 `TextField` 와 같은 하단선만 쓴다 — 사각 박스를 두르면 박스 장식 금지에 걸린다.
 */
export function TextArea({
  label,
  value,
  onChange,
  placeholder = "",
  rows = 6,
  disabled = false,
  maxLength,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
  disabled?: boolean;
  maxLength?: number;
}) {
  return (
    <label className="block">
      <span className="text-xs text-gray-500">{label}</span>
      <textarea
        value={value}
        rows={rows}
        placeholder={placeholder}
        disabled={disabled}
        maxLength={maxLength}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full resize-y border-b border-gray-200 bg-transparent py-1.5 text-sm leading-relaxed text-gray-900 transition-colors placeholder:text-gray-300 focus:border-primary-600 focus:outline-none disabled:text-gray-400"
      />
    </label>
  );
}

export interface SelectOption {
  label: string;
  value: string;
  hint?: string;
}

/** 범주형 입력은 예외 없이 dropdown + 검색 (CLAUDE.md §UI 3) */
export function SelectField({
  label,
  value,
  options,
  onChange,
  placeholder = "전체",
  width = "w-44",
  allowEmpty = true,
  emptyLabel = "전체",
  disabled = false,
  compact = false,
}: {
  label: string;
  value: string;
  options: readonly SelectOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  width?: string;
  allowEmpty?: boolean;
  emptyLabel?: string;
  disabled?: boolean;
  /**
   * 표 액션 줄(`DataTable` 의 `actions`)에 둘 때. 라벨을 위로 쌓지 않고 왼쪽에 붙여
   * 캡션과 한 줄에 서게 한다 — 쌓으면 그 줄만 53px 가 되어 표 머리가 밀린다.
   */
  compact?: boolean;
  /** 조회 조건이 아니라 입력값이면 `false` — 활성 필터 칩에서 뺀다 (`FilterBar` 참고) */
  clearable?: boolean;
}) {
  const dropdown = (
    <SearchableDropdown<string>
      value={value}
      options={options}
      onChange={onChange}
      placeholder={placeholder}
      allowEmpty={allowEmpty}
      emptyLabel={emptyLabel}
      disabled={disabled}
      compact={compact}
    />
  );

  if (compact) {
    return (
      <label className="flex items-center gap-2">
        <span className="shrink-0 text-xs text-gray-500">{label}</span>
        <span className={width}>{dropdown}</span>
      </label>
    );
  }

  return (
    <Field label={label} width={width}>
      {dropdown}
    </Field>
  );
}

// ---------------------------------------------------------------------------
// 버튼·메시지
// ---------------------------------------------------------------------------

/**
 * 보조 동작 버튼 — 저장·보류·연결처럼 주 동작 옆에 서는 것들.
 *
 * 글자만 두면 **본문 텍스트와 구분이 되지 않아** 누를 수 있는 자리인지 알 수 없었다.
 * 그래서 테두리와 여백으로 면을 준다. 주색은 쓰지 않는다 — 주색 면은 그 화면의
 * 주 동작 하나(`PrimaryButton`)에만 준다. 생김새는 `base/buttonStyles.ts` 가 기준이다.
 *
 * 크기는 좌우 여백만 다르고 **높이는 26px 로 같다** (`base/buttonStyles.ts`).
 * - `default` : 화면 머리말·모달 바닥·본문 아래처럼 여백이 있는 자리
 * - `small`   : 표 옆, 필터바 옆처럼 좌우까지 좁혀야 하는 자리
 * - `compact` : 표 안·작은 라벨(`text-xs`) 옆. 여기만 18px 로 더 낮다
 */
export function TextButton({
  onClick,
  children,
  disabled = false,
  strong = false,
  type = "button",
  size = "default",
  title,
}: {
  onClick?: () => void;
  children: ReactNode;
  disabled?: boolean;
  /** 같은 줄의 다른 보조 버튼보다 한 단계 앞세운다 (테두리를 진하게) */
  strong?: boolean;
  type?: "button" | "submit";
  size?: "default" | "small" | "compact";
  /**
   * 마우스를 올렸을 때 뜨는 설명. **왜 지금 누를 수 없는지**를 적는 자리다 —
   * 비활성 버튼은 이유를 말해 주지 않으면 고장으로 보인다.
   */
  title?: string;
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`${buttonClass("secondary", size)} ${
        strong ? "border-gray-900 font-medium text-gray-900 hover:bg-gray-100" : ""
      }`}
    >
      {children}
    </button>
  );
}

/**
 * 주 동작 버튼 — 등록·저장·확정처럼 그 자리에서 일을 확정하는 동작에 쓴다.
 *
 * 검은 면으로 뒀더니 보조 버튼과 구분이 약하다는 지적을 받아 **주색(teal) 면**으로 바꿨다.
 * 색은 1차 프로젝트(`hanjung-driver`)의 `kTealColor` 와 같은 값이다 (CLAUDE.md §주색).
 * **한 화면에 하나만 둔다.** 둘 이상이면 어디를 눌러야 할지 다시 모르게 된다.
 *
 * 크기는 `TextButton` 과 같은 26px 이다. 주 동작이라고 키우지 않는다 —
 * 한 줄에 선 버튼들의 높이가 어긋나면 줄이 들쭉날쭉해 보인다. 눈에 띄는 일은 주색 면이 한다.
 * - `default` : 화면 머리말·모달 바닥처럼 여백이 있는 자리. 좌우만 한 단계 넓다
 * - `small`   : 표 옆·필터바 옆. 같은 주색 면을 쓰되 좌우 여백만 줄인다
 */
export function PrimaryButton({
  onClick,
  children,
  disabled = false,
  type = "button",
  size = "default",
  title,
}: {
  onClick?: () => void;
  children: ReactNode;
  disabled?: boolean;
  type?: "button" | "submit";
  size?: "default" | "small" | "compact";
  /** 왜 지금 누를 수 없는지 (`TextButton` 과 같다) */
  title?: string;
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={buttonClass("primary", size)}
    >
      {children}
    </button>
  );
}

/**
 * 표에 입력 줄을 여닫는 토글 버튼.
 *
 * 닫혀 있을 때(`신규 등록`)는 그 화면에서 새로 만드는 유일한 진입점이라 주색 버튼으로 띄우고,
 * 열려 있을 때(`신규 등록 취소`)는 되돌리는 동작이라 평범한 텍스트 버튼으로 낮춘다.
 * 취소를 주색으로 두면 "여기를 눌러야 한다"는 잘못된 신호를 준다.
 */
export function DraftToggleButton({
  open,
  onClick,
  label,
  cancelLabel,
  size = "small",
  variant = "primary",
}: {
  open: boolean;
  onClick: () => void;
  label: string;
  cancelLabel: string;
  /** 머리말 영역에 두는 경우만 `default` — 필터바·표 옆은 `small`, 작은 라벨 옆은 `compact` */
  size?: "default" | "small" | "compact";
  /**
   * 같은 줄에 더 앞선 주 동작이 있으면 `secondary` 로 내린다.
   * 한 화면에 주색으로 채운 버튼이 둘이면 위계가 무너진다(CLAUDE.md §주색).
   */
  variant?: "primary" | "secondary";
}) {
  if (open) {
    return <TextButton onClick={onClick}>{cancelLabel}</TextButton>;
  }
  if (variant === "secondary") {
    return (
      <TextButton size={size} onClick={onClick}>
        {label}
      </TextButton>
    );
  }
  return (
    <PrimaryButton size={size} onClick={onClick}>
      {label}
    </PrimaryButton>
  );
}

/**
 * 실패만 알린다 — 저장 성공은 조용히 반영한다.
 *
 * **언제나 실패 톤이다.** 조회 실패·입력 검증처럼 톤이 고정인 자리에만 남겨 두고,
 * 성공과 실패가 같은 자리에 번갈아 뜨는 곳은 `useStatusMessage` + `StatusMessage` 를 쓴다
 * (2026-09-09 상태색 도입).
 *
 * `tone` + children 모양이라 **흐름 안에 남는다** — 누른 결과가 아니라 그 폼의 지금
 * 상태이고, 어느 입력칸이 틀렸는지 옆에서 읽혀야 한다 (2026-10-02).
 */
export function ErrorText({ children }: { children: ReactNode }) {
  if (!children) return null;
  return <StatusMessage tone="error">{children}</StatusMessage>;
}

/** `useStatusMessage().value` 인지 그냥 노드인지 가른다 */
function isStatusValue(value: unknown): value is StatusMessageValue {
  return typeof value === "object" && value !== null && "tone" in value && "text" in value;
}

/**
 * `PageHeading` 의 `notice` 를 **경고 톤**으로 올린다.
 *
 * 그 자리의 기본은 안내(`info`)다 — 읽어 둘 설명으로 더 많이 쓰인다. 담당자가 손대야
 * 비로소 다음으로 넘어가는 문구만 이걸로 감싼다(미분류 송장·재제출·중복·반려).
 * 값이 없으면 `undefined` 를 그대로 돌려줘 호출부가 삼항 연산을 그대로 쓸 수 있다.
 */
export function warnNotice(text: ReactNode): StatusMessageValue | undefined {
  // `inline` — 이것은 누른 결과가 아니라 **그 건의 상태**다. 떠 있는 알림은 4초 뒤
  // 사라지거나 담당자가 내리는 것이라, 화면을 열어 둔 동안 계속 보여야 하는 문구를
  // 담을 수 없다 (2026-10-02).
  return text ? { tone: "warn", text, inline: true } : undefined;
}

/**
 * 머리말 경고 + 서버가 준 경고 줄.
 *
 * `warnNotice` 와 같은 자리이되 **서버 문구를 아래 줄로 함께 낸다**. 둘을 따로 그리면
 * 상자가 둘이 되고, 상자는 이 프로젝트에서 아껴 쓰는 장치다(CLAUDE.md §UI 1).
 * 화면이 계산한 안내가 먼저고 서버 경고가 뒤다 — 앞의 것이 대개 "지금 무엇을 하라" 이다.
 */
export function statusNotice(
  text: ReactNode,
  warnings?: string[]
): StatusMessageValue | undefined {
  if (!text && !warnings?.length) return undefined;
  // `warnNotice` 와 같은 이유로 흐름 안에 남는다 — 송장 검수의 이 상자는 본문 3~4줄 +
  // 서버 경고 줄까지 90~140px 이라 띄울 크기도 아니다.
  return { tone: "warn", text, warnings, inline: true };
}

export function HintText({ children }: { children: ReactNode }) {
  return <p className="text-xs leading-relaxed text-gray-400">{children}</p>;
}

// ---------------------------------------------------------------------------
// 페이지네이션
// ---------------------------------------------------------------------------

export function Pagination({
  page,
  size,
  total,
  onChange,
}: {
  page: number;
  size: number;
  total: number;
  onChange: (page: number) => void;
}) {
  const lastPage = Math.max(1, Math.ceil(total / Math.max(size, 1)));
  const start = total === 0 ? 0 : (page - 1) * size + 1;
  const end = Math.min(page * size, total);

  return (
    <div className="mt-3 flex items-center justify-between text-xs text-gray-400">
      <div>
        {total.toLocaleString()}건 중 {start.toLocaleString()}–{end.toLocaleString()}
      </div>
      {/* 12px 글자 줄이라 좌우 여백까지 좁힌다 — 높이는 기본 버튼과 같은 26px */}
      <div className="flex items-center gap-3">
        <TextButton size="small" onClick={() => onChange(page - 1)} disabled={page <= 1}>
          이전
        </TextButton>
        <span className="tabular-nums text-gray-500">
          {page} / {lastPage}
        </span>
        <TextButton size="small" onClick={() => onChange(page + 1)} disabled={page >= lastPage}>
          다음
        </TextButton>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 모달 — 배정 기간 충돌처럼 반드시 눈에 띄어야 하는 안내에만 쓴다
// ---------------------------------------------------------------------------

export function Modal({
  title,
  onClose,
  children,
  footer,
  width = "max-w-xl",
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  width?: string;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div data-hj-modal className="fixed inset-0 z-50 flex items-start justify-center bg-black/20 px-6 py-24">
      <div className={`w-full ${width} border border-gray-900 bg-white`}>
        <div className="flex items-baseline justify-between border-b border-gray-200 px-6 py-4">
          <h3 className="text-sm font-medium text-gray-900">{title}</h3>
          <TextButton onClick={onClose}>닫기</TextButton>
        </div>
        <div className="max-h-[60vh] overflow-y-auto px-6 py-5">{children}</div>
        {footer && (
          <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-6 py-4 text-sm">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 신규 등록 행 — 표 안에서 바로 입력받는다
// ---------------------------------------------------------------------------

export function DraftInput({
  value,
  onChange,
  placeholder = "",
  type = "text",
  align = "left",
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: "text" | "number" | "date" | "month";
  align?: "left" | "right";
}) {
  return (
    <input
      type={type}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className={`w-full min-w-[4rem] border-b border-gray-300 bg-transparent px-0 py-0.5 text-sm tabular-nums placeholder:text-gray-300 focus:border-primary-600 focus:outline-none ${
        align === "right" ? "text-right" : "text-left"
      }`}
    />
  );
}

export function DraftSelect({
  value,
  options,
  onChange,
  placeholder = "선택",
  allowEmpty = false,
  emptyLabel = "(없음)",
}: {
  value: string;
  options: readonly SelectOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  allowEmpty?: boolean;
  emptyLabel?: string;
}) {
  return (
    <SearchableDropdown<string>
      value={value}
      options={options}
      onChange={onChange}
      placeholder={placeholder}
      allowEmpty={allowEmpty}
      emptyLabel={emptyLabel}
    />
  );
}

// ---------------------------------------------------------------------------
// 공통 옵션·헬퍼
// ---------------------------------------------------------------------------

export const ACTIVE_OPTIONS: SelectOption[] = [
  { label: "사용", value: "true" },
  { label: "미사용", value: "false" },
];

/**
 * **지울 수 있는 것만 보기** (고객 요청 2026-10-14 — 장소·구간을 정리하려는데 600줄이다).
 *
 * `사용 여부`(미사용)와 **다른 축**이다 — 이쪽은 *아무도 안 쓰는 것*, 저쪽은 *담당자가
 * 목록에서 뺀 것*. 한 칸에 섞으면 *"미사용인데 왜 못 지우나"* 가 된다.
 */
export const UNUSED_OPTIONS: SelectOption[] = [
  { label: "안 쓰는 것만", value: "unused" },
];

/**
 * 구간 화면의 `정리` 선택지 — **지울 것**과 **고칠 것** 둘 (2026-10-16).
 *
 * `미사용 장소 쓰는 것만` 은 *구간은 살아 있는데 그 상차지·하차지가 꺼져 있는* 줄이다.
 * 장소를 `미사용` 으로 돌려도 그 장소를 쓰는 구간은 그대로 살아 있는데, 담당자가 정리를
 * 끝냈는지 확인할 자리가 없었다 — 상행 상차지를 옮긴 다음 날 MES 반영이 미사용 장소로
 * 구간 5개를 새로 만든 것이 그 상태를 아무도 못 본 결과다(고객 신고 2026-10-16).
 *
 * 둘 다 *정리할 거리를 찾는* 축이라 한 칸에 둔다. `사용 여부`(구간 자체가 꺼져 있다)와는
 * 여전히 다른 축이라 섞지 않는다. **새 칸을 만들지 않은 이유**는 구간 조회조건이 이미
 * 일곱 칸이라 여덟 번째를 더하면 1440px 에서 줄이 찢어지기 때문이다.
 */
export const ROUTE_CLEANUP_OPTIONS: SelectOption[] = [
  { label: "안 쓰는 것만", value: "unused" },
  { label: "미사용 장소 쓰는 것만", value: "inactive-place" },
];

/**
 * 일괄 삭제 결과를 담당자가 읽을 문장으로 바꾼다 (2026-10-15).
 *
 * **건너뛴 줄을 조용히 넘기지 않는다** — 수백 건을 한 번에 지우면 무엇이 남았는지
 * 눈으로 셀 수 없다. 사유는 **처음 셋만** 적는다(그 이상은 문장이 화면을 덮는다).
 */
export function bulkDeleteMessage(
  result: {
    deleted: number;
    skipped: { label: string; reason: string }[];
    missing: number[];
    prices_deleted?: number;
    locations_deleted?: number;
    routes_deleted?: number;
  },
  unit: string
): [string, string[]] {
  const head = [`${result.deleted.toLocaleString()}${unit}을 지웠습니다`];
  if (result.routes_deleted) head.push(`구간 ${result.routes_deleted.toLocaleString()}건 함께 삭제`);
  if (result.prices_deleted) head.push(`단가 ${result.prices_deleted.toLocaleString()}건 함께 삭제`);
  if (result.locations_deleted) head.push(`장소 ${result.locations_deleted.toLocaleString()}곳 함께 삭제`);

  const details: string[] = [];
  if (result.skipped.length) {
    details.push(`${result.skipped.length}${unit}은 쓰고 있어 건너뛰었습니다`);
    for (const row of result.skipped.slice(0, 3)) details.push(`${row.label} — ${row.reason}`);
    if (result.skipped.length > 3) details.push(`외 ${result.skipped.length - 3}건`);
  }
  if (result.missing.length) details.push(`찾지 못한 줄 ${result.missing.length}건`);
  return [head.join(" · "), details];
}

/**
 * 기준정보 삭제 확인 (장소·구간, 2026-10-14).
 *
 * 되돌릴 수 없는 조작이라 한 번 묻는다 — 거래처·단가 삭제와 같은 꼴이다.
 * **함께 지워지는 것이 있으면 `extra` 로 적는다** — 구간은 그 구간의 단가를 함께
 * 지우므로, 조용히 지우면 계약 자료가 사라진 것을 모른다.
 */
export function DeleteMasterModal({
  title,
  label,
  extra,
  hint,
  blocked,
  pending,
  onClose,
  onConfirm,
}: {
  title: string;
  label: string;
  extra?: ReactNode;
  hint?: ReactNode;
  /**
   * 지금은 지울 수 없는 사유. 주면 `지우기` 를 잠근다 — 서버가 어차피 409 로 막으므로
   * 눌러 보게 두면 **왜 안 되는지를 실패 메시지로** 알게 된다(고객 요청 2026-10-15 의
   * 미리보기가 그 한 번을 없앤다).
   */
  blocked?: string | null;
  pending: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <Modal
      title={title}
      onClose={onClose}
      footer={
        <>
          <TextButton onClick={onClose}>취소</TextButton>
          <PrimaryButton onClick={onConfirm} disabled={pending || Boolean(blocked)}>
            {pending ? "삭제 중" : "지우기"}
          </PrimaryButton>
        </>
      }
    >
      <p className="text-sm text-gray-700">{label} 을(를) 지웁니다. 되돌릴 수 없습니다.</p>
      {extra}
      {blocked && <p className="mt-2 text-sm text-gray-900">{blocked}</p>}
      {hint && <p className="mt-2 text-xs text-gray-500">{hint}</p>}
    </Modal>
  );
}


// ---------------------------------------------------------------------------
// 엇갈린 연쇄 삭제 미리보기 (고객 요청 2026-10-15)
// ---------------------------------------------------------------------------
//
// > *"구간 삭제시 관련 장소 데이터도 함께 삭제하거나, 장소 삭제시 관련 구간도 같이 삭제"*
//
// 한 번 눌러 **여러 종류**가 사라진다. 그래서 확인창이 무엇이 사라지는지 세어 적고,
// 못 지우는 줄은 **그 줄마다 사유**를 적는다 — 조용히 지우면 계약 자료가 사라진 것을
// 모르고, 뭉뚱그려 막으면 무엇을 고쳐야 하는지 모른다.

const CASCADE_WORDS = {
  routes: { label: "관련 구간도 함께 지우기", unit: "건", noun: "구간" },
  locations: { label: "관련 장소도 함께 지우기", unit: "곳", noun: "장소" },
} as const;

/** 지울 수 없으면 그 사유, 지울 수 있으면 `null`. 모달의 `blocked` 에 그대로 넣는다 */
export function cascadeBlockedReason(
  plan: DeletePlan | undefined,
  kind: "routes" | "locations",
  enabled: boolean
): string | null {
  if (!plan) return null;
  if (plan.blocked_by) return `${plan.blocked_by}에서 쓰고 있어 지울 수 없습니다.`;
  if (kind === "locations") return null; // 구간→장소 쪽은 장소가 막는 일이 없다
  const blocked = plan.routes.filter((row) => row.blocked_by);
  if (!enabled && plan.routes.length)
    return `구간 ${plan.routes.length.toLocaleString()}건이 쓰고 있습니다. ‘${CASCADE_WORDS.routes.label}’ 를 켜거나 그 구간을 먼저 지우세요.`;
  if (enabled && blocked.length)
    return `쓰고 있는 구간 ${blocked.length.toLocaleString()}건이 있어 지울 수 없습니다.`;
  return null;
}

export function CascadePreview({
  plan,
  loading,
  kind,
  enabled,
  onToggle,
}: {
  plan?: DeletePlan;
  loading: boolean;
  /** `routes` = 장소를 지우며 구간을 함께 · `locations` = 구간을 지우며 장소를 함께 */
  kind: "routes" | "locations";
  enabled: boolean;
  onToggle: (value: boolean) => void;
}) {
  const words = CASCADE_WORDS[kind];
  if (loading) return <p className="mt-2 text-xs text-gray-500">함께 지워질 것을 세는 중입니다…</p>;
  if (!plan) return null;

  const rows = kind === "routes" ? plan.routes : plan.locations;
  const blocked = rows.filter((row) => row.blocked_by);
  const together = [
    rows.length && enabled ? `${words.noun} ${rows.length.toLocaleString()}${words.unit}` : "",
    plan.prices ? `단가 ${plan.prices.toLocaleString()}건` : "",
    plan.aliases ? `별칭 ${plan.aliases.toLocaleString()}개` : "",
  ].filter(Boolean);

  return (
    <div className="mt-3">
      {rows.length > 0 && (
        <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => onToggle(e.target.checked)}
            className="h-3.5 w-3.5 cursor-pointer accent-primary-600"
          />
          {words.label} ({rows.length.toLocaleString()}{words.unit})
        </label>
      )}
      {together.length > 0 && (
        <p className="mt-2 text-xs text-gray-500">함께 지워집니다 — {together.join(" · ")}</p>
      )}
      {rows.length > 0 && (
        <ul className="mt-2 max-h-40 overflow-y-auto border-y border-gray-200 py-1.5 text-xs">
          {rows.slice(0, 50).map((row) => (
            <li key={row.id} className="flex gap-2 py-0.5">
              <span className={row.blocked_by ? "text-gray-400 line-through" : "text-gray-700"}>
                {row.label}
              </span>
              {row.blocked_by && <span className="text-gray-900">{row.blocked_by}</span>}
            </li>
          ))}
          {rows.length > 50 && (
            <li className="py-0.5 text-gray-500">외 {(rows.length - 50).toLocaleString()}{words.unit}</li>
          )}
        </ul>
      )}
      {blocked.length > 0 && (
        <p className="mt-2 text-xs text-gray-500">
          줄 그어진 {blocked.length.toLocaleString()}{words.unit}은 실적·배차 등에 걸려 있어
          지울 수 없습니다. 목록에서만 빼시려면 ‘미사용’ 으로 돌려 주세요.
        </p>
      )}
    </div>
  );
}

export const YES_NO_OPTIONS: SelectOption[] = [
  { label: "예", value: "true" },
  { label: "아니오", value: "false" },
];

export function activeLabel(value: boolean | null | undefined): string {
  return value ? "사용" : "미사용";
}

/**
 * 두 상태 중 하나를 고르는 칸.
 *
 * 예전에는 **지금 상태를 적은 버튼 하나**였다 — `사용` 이라고 적힌 버튼을 누르면 `미사용` 이
 * 되는 식이다. 담당자가 "사용하겠다는 뜻"으로 읽고 눌러 거꾸로 꺼 버리는 일이 생겼다
 * (사용자 지적 2026-08-27). 버튼의 글자가 **상태이자 동작**이라 둘 중 무엇인지 알 수 없었다.
 *
 * 그래서 상태와 동작을 분리한다. 두 칸을 나란히 두고 **지금 상태를 고른 것으로 표시**하고,
 * 누르는 것은 언제나 **그 상태로 만들겠다**는 뜻이다. 이미 그 상태인 쪽은 눌리지 않는다.
 * 고른 것 표시에 주색을 쓰는 것은 사이드바 현재 메뉴·탭·밀도 컨트롤과 같은 규칙이다.
 */
export function SegmentedToggle<T extends string | number | boolean>({
  value,
  options,
  onChange,
  size = "small",
  label,
  disabled = false,
  busy = false,
}: {
  value: T;
  /** 두 칸. 순서대로 그린다 */
  options: readonly [{ value: T; label: string }, { value: T; label: string }];
  /** 누른 쪽 값으로 바꾼다. 이미 그 값이면 부르지 않는다 */
  onChange: (next: T) => void;
  size?: "small" | "compact";
  /** 스크린리더용 묶음 이름 */
  label: string;
  disabled?: boolean;
  /** 저장 중 — 두 칸을 다 잠그고 고른 칸에 진행 점(`Working`)을 띄운다 */
  busy?: boolean;
}) {
  return (
    <span
      className="inline-flex items-center gap-1"
      role="group"
      aria-label={label}
      aria-busy={busy || undefined}
    >
      {options.map((option) => {
        const current = value === option.value;
        return (
          <button
            key={String(option.value)}
            type="button"
            disabled={disabled || busy || current}
            aria-pressed={current}
            onClick={() => onChange(option.value)}
            className={`${buttonClass("secondary", size)} ${
              current
                ? "border-primary-600 bg-primary-50 font-medium text-primary-700 " +
                  "disabled:border-primary-600 disabled:bg-primary-50 disabled:text-primary-700"
                : ""
            }`}
          >
            {busy && current ? <Working>{option.label}</Working> : option.label}
          </button>
        );
      })}
    </span>
  );
}

/**
 * 자사 직영 / 업체 전환 (기준정보 › 차량).
 *
 * **이 값이 MES 실적의 갈림길이다.** 자사로 두면 배차와 안 붙은 MES 행이 `미매칭` 으로
 * 남아 담당자가 배차를 등록하게 되고, 업체로 두면 소속 운송사에 귀속돼 그대로 실적이 된다.
 * 그래서 화면에서 보이고 고칠 수 있어야 한다.
 *
 * `ActiveToggle` 과 같은 두 칸 컨트롤이다 — 지금 상태가 고른 것으로 보이고, 누르면 그 상태가
 * 된다. 상태를 적은 버튼 하나로 만들면 거꾸로 누르는 사고가 난다(2026-08-27 사용/미사용 사고).
 */
export function OwnFleetToggle({
  value,
  onChange,
  disabled = false,
}: {
  value: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <SegmentedToggle<boolean>
      label="자사 여부"
      value={value}
      options={[
        { value: true, label: "자사" },
        { value: false, label: "업체" },
      ]}
      onChange={onChange}
      disabled={disabled}
    />
  );
}

/** 사용 여부 전환 (기준정보 표의 마지막 칸) — `SegmentedToggle` 참고 */
export function ActiveToggle({
  value,
  onChange,
  disabled = false,
}: {
  value: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
}) {
  /**
   * **누른 값을 바로 보여 준다**(고객 보고 2026-10-28 — *"사용 미사용 버튼 한번에
   * 안바뀌고 여러번 눌러야 바뀝니다"*). 저장·재조회가 끝날 때까지 옛 값이 떠 있으면
   * 안 눌린 줄 알고 또 누른다. 서버 값이 따라오면 내려놓고, 저장이 실패해 끝내 안
   * 따라오면 8초 뒤 서버 값으로 돌아간다.
   *
   * **그동안 두 칸을 다 잠근다**(담당자 요청 2026-10-29 — *"한 번 클릭하면 여러번 다시 클릭
   * 못하게"*). 저장 중에 반대 칸을 누르면 요청 둘이 엇갈려 마지막에 도착한 쪽이 남는다.
   * 진행 표시는 스피너가 아니라 `Working` 의 점이다(§UI 1 — 아이콘 금지).
   */
  const [pending, setPending] = useState<boolean | null>(null);
  useEffect(() => {
    if (pending === null) return;
    if (value === pending) {
      setPending(null);
      return;
    }
    const timer = window.setTimeout(() => setPending(null), 8000);
    return () => window.clearTimeout(timer);
  }, [value, pending]);
  return (
    <SegmentedToggle<boolean>
      label="사용 여부"
      value={pending ?? value}
      options={[
        { value: true, label: "사용" },
        { value: false, label: "미사용" },
      ]}
      onChange={(next) => {
        setPending(next);
        onChange(next);
      }}
      disabled={disabled}
      busy={pending !== null}
    />
  );
}

export function toOptions<T>(
  rows: readonly T[] | undefined,
  value: (row: T) => number | string,
  label: (row: T) => string,
  hint?: (row: T) => string | undefined
): SelectOption[] {
  return (rows ?? []).map((row) => ({
    value: String(value(row)),
    label: label(row),
    hint: hint?.(row),
  }));
}

/**
 * 옵션 보조 설명 조립.
 * 드롭다운에는 미사용 항목도 담는다(기존 행이 참조 중일 수 있다) — 대신 여기서 표시한다.
 */
export function optionHint(...parts: (string | false | null | undefined)[]): string | undefined {
  const joined = parts.filter(Boolean).join(" · ");
  return joined === "" ? undefined : joined;
}

/** 빈 문자열은 undefined 로, 숫자 문자열은 number 로 */
export function numOrUndef(value: string): number | undefined {
  if (value.trim() === "") return undefined;
  const num = Number(value);
  return Number.isNaN(num) ? undefined : num;
}

export function strOrNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

/** 검색어처럼 타이핑마다 조회하면 안 되는 값에 쓴다 */
export function useDebounced<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

